import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiShield, FiUsers, FiAward } = FiIcons;

const About = () => {
  const values = [
    {
      icon: FiHeart,
      title: 'Passion for Quality',
      description: 'Every brushstroke matters. I take pride in delivering flawless finishes that stand the test of time.'
    },
    {
      icon: FiShield,
      title: 'Trust & Reliability',
      description: 'Fully insured and licensed. I treat your home with the same care and respect I would my own.'
    },
    {
      icon: FiUsers,
      title: 'Personal Service',
      description: 'You work directly with me, not a middleman. Clear communication from start to finish.'
    },
    {
      icon: FiAward,
      title: 'Satisfaction Guaranteed',
      description: 'Not happy with the results? I\'ll make it right. Your satisfaction is my top priority.'
    }
  ];

  return (
    <section id="about" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content - Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80"
                alt="Beautiful painted interior"
                className="w-full h-96 object-cover"
              />
            </div>
            {/* Experience Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="absolute -top-6 -right-6 bg-blue-600 text-white p-6 rounded-xl shadow-xl"
            >
              <div className="text-center">
                <div className="text-3xl font-bold">20+</div>
                <div className="text-sm opacity-90">Years of Excellence</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Content - About Text */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  Hi, I'm Edwin Reinoso
                </h2>
                <div className="w-20 h-1 bg-blue-600 rounded-full"></div>
              </div>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p className="text-lg">
                  For over two decades, I've dedicated myself to the art of painting, transforming homes and spaces with precision and care. What started as a craft has grown into a lifelong passion, allowing me to bring beauty and protection to countless homes in our community.
                </p>
                <p>
                  My journey in painting began 20 years ago, and since then, I've mastered every aspect of interior and exterior painting. From historic home restorations to modern renovations, each project receives my personal attention and expertise. I believe that quality painting is about more than just colors – it's about understanding the unique character of each home and the vision of every homeowner.
                </p>
                <p className="font-medium text-blue-700">
                  As a proud family-owned business, we bring traditional values to every project. Our family has been in the painting industry for generations, passing down techniques and knowledge that can't be taught in books. We believe in strong work ethic, attention to detail, and treating each customer's home with the utmost respect.
                </p>
                <p>
                  When you choose to work with me, you're not just hiring a painter – you're partnering with a family business that genuinely cares about bringing your vision to life. We take pride in our attention to detail, use of premium materials, and commitment to exceeding expectations on every project.
                </p>
              </div>
            </div>

            {/* Values Grid */}
            <div className="grid sm:grid-cols-2 gap-6">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white p-4 rounded-lg shadow-sm border border-gray-100"
                >
                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <SafeIcon icon={value.icon} className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{value.title}</h3>
                      <p className="text-sm text-gray-600">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;